import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { F, Sel } from "@/components/admin/RatesAdmin";

const empty = { name: "", code: "", country: "", region: "", type: "both", active: true };

export default function PortsAdmin() {
  const [ports, setPorts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/ports").then(({ data }) => setPorts(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.code) { toast.error("Name and code required"); return; }
    try {
      if (editId) await api.put(`/ports/${editId}`, form);
      else await api.post("/ports", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { await api.delete(`/ports/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" className="bg-signal text-white rounded-sm" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-port-btn"><Plus className="h-4 w-4 mr-1.5" />Add Port</Button>
      </div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-navy text-white text-xs uppercase">
            <th className="text-left px-3 py-2.5">Port</th><th className="text-left px-3 py-2.5">Code</th>
            <th className="text-left px-3 py-2.5">Country</th><th className="text-left px-3 py-2.5">Region</th><th></th>
          </tr></thead>
          <tbody>
            {ports.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-secondary/50" data-testid={`admin-port-${p.id}`}>
                <td className="px-3 py-2.5 text-navy font-medium">{p.name}</td>
                <td className="px-3 py-2.5 font-mono-data text-xs">{p.code}</td>
                <td className="px-3 py-2.5">{p.country}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{p.region}</td>
                <td className="px-3 py-2.5"><div className="flex gap-1 justify-end">
                  <button onClick={() => { setForm(p); setEditId(p.id); setOpen(true); }} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`edit-port-${p.id}`}><Pencil className="h-3.5 w-3.5 text-navy" /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 hover:bg-secondary rounded-sm" data-testid={`del-port-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} Port</DialogTitle><DialogDescription className="sr-only">Port details form</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Port Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" data-testid="pf-name" /></F>
            <F label="Port Code"><Input value={form.code} onChange={(e) => set("code", e.target.value)} className="rounded-sm font-mono-data" data-testid="pf-code" /></F>
            <F label="Country"><Input value={form.country} onChange={(e) => set("country", e.target.value)} className="rounded-sm" data-testid="pf-country" /></F>
            <F label="Region"><Input value={form.region} onChange={(e) => set("region", e.target.value)} className="rounded-sm" data-testid="pf-region" /></F>
            <F label="Type"><Sel value={form.type} onChange={(v) => set("type", v)} items={[{ v: "both", l: "Export & Import" }, { v: "export", l: "Export" }, { v: "import", l: "Import" }]} testid="pf-type" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-signal text-white rounded-sm" onClick={save} data-testid="pf-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
